// Stage 1 of the routing migration (docs/plans/toasty-soaring-pudding.md) — the
// first module moved off the legacy $.tsx switch onto a real file route with its
// own code-split chunk. See events.index.tsx for the list half of this pair.
import React from 'react';
import { Loader2 } from 'lucide-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { cacheEntity, readCachedEntity, pathForRoute } from '../lib/router';
import { resolveRouteEntity } from '../lib/queries/entityById';
import { patchEvent, invalidateEventsList } from '../lib/queries/events';
import { EventDetailPageV2 } from '../modules/events/EventDetailPageV2';
import { DetailNotFound } from '../components/DetailNotFound';
import { useShell } from '../lib/shellContext';

export const Route = createFileRoute('/events/$id')({
  loader: async ({ params }) => {
    // Warm path: a list row already cached this exact entity (sessionStorage,
    // survives reload) — render it instantly, then revalidate in the
    // background. Cold path (shared link, empty cache): wait for the real
    // fetch, same as the legacy resolver did.
    const warm = readCachedEntity('event-detail', params.id);
    const revalidate = resolveRouteEntity('event-detail', params.id).then((e) => {
      if (e) cacheEntity('event-detail', e);
      return e;
    });
    if (warm) return { event: warm };
    return { event: await revalidate };
  },
  pendingComponent: () => (
    <div className="flex items-center justify-center py-32 text-[13px] text-[#6B7280]">
      <Loader2 className="w-4 h-4 animate-spin mr-2" />Loading…
    </div>
  ),
  component: EventDetailRoute,
});

function EventDetailRoute() {
  const { id } = Route.useParams();
  const { event: loaderEvent } = Route.useLoaderData();
  const navigate = useNavigate();
  const { setGlobalEventFilter } = useShell();

  // Subscribes to the SAME cache entry the loader seeded (['events','detail',id],
  // identical to qk.events.detail(id)) — patchEvent's setQueryData on that key
  // (called from onEventUpdated below) flows straight into `event` reactively,
  // no local state needed.
  const { data: event } = useQuery({
    queryKey: ['events', 'detail', id],
    queryFn: () => resolveRouteEntity('event-detail', id),
    initialData: loaderEvent,
    enabled: false,
  });

  if (!event) return <DetailNotFound active="event-detail" routeId={id} />;

  const navToRef = (active) => (ref) => {
    const rid = typeof ref === 'string' ? ref : (ref?.routeId || ref?.id || ref?.code || '');
    if (!rid) return;
    navigate({ to: pathForRoute(active, { id: rid }) });
  };
  // Orders/transactions come from an inline list on this page with the full row
  // already in hand — cache it (same sessionStorage bridge the list pages use)
  // so their detail route (still legacy, not yet migrated) renders instantly.
  const navToFull = (active) => (entity) => { cacheEntity(active, entity); navigate({ to: pathForRoute(active, entity) }); };

  return (
    <EventDetailPageV2
      event={event}
      onBack={() => navigate({ to: pathForRoute('events', null) })}
      onEventUpdated={(next) => { if (next?.id) patchEvent(next); }}
      // "Duplicate event" creates a new row and invalidates the list — it does
      // NOT navigate away, matching the legacy behavior of staying put.
      onEventCreated={() => invalidateEventsList()}
      onNavOrganizer={navToRef('organizer-detail')}
      onNavVenue={navToRef('venue-detail')}
      onNavTxn={navToFull('transaction-detail')}
      onNavOrder={navToFull('order-detail')}
      onNavTicketingInventory={(e) => { setGlobalEventFilter(e || event); navigate({ to: pathForRoute('ticketing-inventory', null) }); }}
      onNavSubscriptions={(e) => { setGlobalEventFilter(e || event); navigate({ to: pathForRoute('subscriptions', null) }); }}
      onNavReports={(e) => { setGlobalEventFilter(e || event); navigate({ to: pathForRoute('reports', null), search: { tab: 'analytics' } }); }}
    />
  );
}
